import * as React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import queryString from 'query-string';
import { Select, Checkbox, Button, Spin, Alert, Empty } from 'antd';
import {
  LinkOutlined,
  DeleteOutlined,
  RightOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FieldTimeOutlined,
  HddOutlined,
} from '@ant-design/icons';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { PATHS } from 'router/config';
import { PopupNames } from 'core';
import { ZoomRecordingFieldsFragment } from 'core/graphql';
import {
  useZoomRecordings,
  useZoomAccounts,
  useZoomMutations,
  usePopupStateOperations,
} from 'core/hooks';
import { todayInWarsaw, toApiDate, formatWarsaw } from 'core/utils/zoomTime';
import { formatBytes, parseZoomFiles } from 'core/utils/format';
import {
  ZoomDateRangeFilter,
  InfiniteScrollContainer,
  LoadingData,
} from 'molecules';
import { Colors } from 'services/ColorService';
import { getFont } from 'services/FontService';

const REFETCH_DELAY = 3_000;

type Row = ZoomRecordingFieldsFragment;
type RecordingState = 'orphan' | 'matched' | 'deleted';

const recordingState = (row: Row): RecordingState | null => {
  if (row.deletedFromZoom) {
    return 'deleted';
  }
  if (row.isMatched) {
    return 'matched';
  }
  if (row.isOrphan) {
    return 'orphan';
  }
  return null;
};

export const RecordingsList = memo(() => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const initial = useMemo(() => queryString.parse(location.search), [
    location.search,
  ]);

  const [range, setRange] = useState<[DateTime, DateTime]>(() => {
    if (typeof initial.date === 'string') {
      const day = DateTime.fromISO(initial.date);
      return [day, day];
    }
    return [todayInWarsaw().minus({ days: 6 }), todayInWarsaw()];
  });
  const [accountId, setAccountId] = useState<string | undefined>();
  const [orphanOnly, setOrphanOnly] = useState(initial.orphanOnly === 'true');
  const [tooShort, setTooShort] = useState(initial.tooShort === 'true');

  const filters = useMemo(
    () => ({
      dateFrom: toApiDate(range[0]),
      dateTo: toApiDate(range[1]),
      accountId,
      orphanOnly: orphanOnly || undefined,
      tooShort: tooShort || undefined,
    }),
    [range, accountId, orphanOnly, tooShort],
  );

  const { data, loading, error, hasNextPage, operations } = useZoomRecordings(
    filters,
  );
  const { data: accounts } = useZoomAccounts();
  const mutations = useZoomMutations();
  const {
    operations: { showPopup },
  } = usePopupStateOperations();

  const refetchSoon = useCallback(() => {
    window.setTimeout(operations.refetch, REFETCH_DELAY);
  }, [operations]);

  const onBind = useCallback(
    (row: Row) => {
      const day = formatWarsaw(row.startTime, 'yyyy-LL-dd');
      history.push(`${PATHS.zoomMatching}?date=${day}`);
    },
    [history],
  );

  const onDelete = useCallback(
    (row: Row) => {
      const confirmString = `DELETE ${row.id}`;
      showPopup(PopupNames.zoomDangerConfirm, {
        title: `${t('pages.zoom.recordings.deleteTitle')}`,
        body: `${t('pages.zoom.recordings.deleteBody')}`,
        confirmString,
        onConfirm: async () => {
          await mutations.deleteRecording.run(row.id, confirmString);
          refetchSoon();
        },
      });
    },
    [t, showPopup, mutations, refetchSoon],
  );

  const renderItem = (row: Row) => {
    const state = recordingState(row);
    const files = parseZoomFiles(row.files);

    return (
      <Card
        key={row.id}
        onClick={() => history.push(`${PATHS.zoomRecordings}/${row.id}`)}
      >
        <CardTop>
          <TitleBlock>
            <Title>{row.topic || t('pages.zoom.recordings.cardTitle')}</Title>
            <Subtitle>
              {row.zoomAccount?.displayName}
              {row.zoomAccount?.email ? ` · ${row.zoomAccount.email}` : ''}
            </Subtitle>
          </TitleBlock>
          <StatusBlock>
            {row.isTooShort ? (
              <TooShortChip>
                {t('pages.zoom.recordings.state.tooShort')}
              </TooShortChip>
            ) : null}
            {state ? (
              <StateChip state={state}>
                {t(`pages.zoom.recordings.state.${state}`)}
              </StateChip>
            ) : null}
            <Chevron>
              <RightOutlined />
            </Chevron>
          </StatusBlock>
        </CardTop>

        <MetaRow>
          <MetaItem>
            <CalendarOutlined /> {formatWarsaw(row.startTime, 'yyyy-LL-dd')}
          </MetaItem>
          <MetaItem>
            <ClockCircleOutlined /> {formatWarsaw(row.startTime, 'HH:mm')}
          </MetaItem>
          <MetaItem>
            <FieldTimeOutlined /> {row.durationMinutes}{' '}
            {t('pages.zoom.recordings.min')}
          </MetaItem>
          <MetaItem>
            <HddOutlined /> {formatBytes(row.totalSizeBytes)}
          </MetaItem>
        </MetaRow>

        {files.length ? (
          <FileChips>
            {files.map((file, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <FileChip key={`${file.file_type}-${index}`}>
                {(file.file_type || '?').toUpperCase()}
              </FileChip>
            ))}
          </FileChips>
        ) : null}

        <Divider />

        <Actions onClick={(event: React.MouseEvent) => event.stopPropagation()}>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            disabled={row.deletedFromZoom || row.isMatched === true}
            onClick={() => onBind(row)}
          >
            {t('pages.zoom.recordings.actions.bind')}
          </Button>
          <Spacer />
          <Button
            danger
            icon={<DeleteOutlined />}
            disabled={row.deletedFromZoom}
            onClick={() => onDelete(row)}
          >
            {t('pages.zoom.recordings.actions.delete')}
          </Button>
        </Actions>
      </Card>
    );
  };

  return (
    <Page>
      <PageHeader>
        <HeaderTitleRow>
          <HeaderTitle>{t('pages.zoom.recordings.title')}</HeaderTitle>
          <HeaderCount>
            {t('pages.zoom.recordings.count', { count: data.length })}
          </HeaderCount>
        </HeaderTitleRow>
        <HeaderSubtitle>
          {t('pages.zoom.recordings.listSubtitle')}
        </HeaderSubtitle>
      </PageHeader>

      <FilterCard>
        <FilterItem>
          <FilterLabel>{t('pages.zoom.recordings.filters.period')}</FilterLabel>
          <ZoomDateRangeFilter
            value={range}
            onChange={(next) => {
              if (next) {
                setRange(next);
              }
            }}
          />
        </FilterItem>
        <FilterItem>
          <FilterLabel>
            {t('pages.zoom.recordings.filters.account')}
          </FilterLabel>
          <Select
            allowClear
            style={{ width: 220 }}
            placeholder={t('pages.zoom.recordings.filters.account')}
            value={accountId}
            onChange={(value) => setAccountId(value as string | undefined)}
            options={accounts.map((account) => ({
              value: account.id,
              label: account.displayName,
            }))}
          />
        </FilterItem>
        <FilterCheckbox>
          <Checkbox
            checked={orphanOnly}
            onChange={(event) => setOrphanOnly(event.target.checked)}
          >
            {t('pages.zoom.recordings.filters.orphanOnly')}
          </Checkbox>
        </FilterCheckbox>
        <FilterCheckbox>
          <Checkbox
            checked={tooShort}
            onChange={(event) => setTooShort(event.target.checked)}
          >
            {t('pages.zoom.recordings.filters.tooShort')}
          </Checkbox>
        </FilterCheckbox>
      </FilterCard>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {loading && !data.length ? (
        <CenteredPanel>
          <Spin />
        </CenteredPanel>
      ) : null}

      {!loading && !data.length ? (
        <CenteredPanel>
          <Empty description={t('pages.zoom.recordings.noData')} />
        </CenteredPanel>
      ) : null}

      {data.length ? (
        <InfiniteScrollContainer
          loadMore={operations.fetchMore}
          hasMore={hasNextPage}
          dataLength={data.length}
          loading={loading}
        >
          <List>{data.map(renderItem)}</List>
        </InfiniteScrollContainer>
      ) : null}

      {Boolean(data.length) && loading ? <LoadingData /> : null}
    </Page>
  );
});

const Page = styled.div`
  padding: 16px 0 40px;
`;

const PageHeader = styled.div`
  margin-bottom: 16px;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h2`
  ${getFont({ fontSize: 22, lineHeight: 28, familyWeight: 'bold' })};
  color: ${Colors.Black};
  margin: 0;
`;

const HeaderCount = styled.span`
  background: ${Colors.BasicBlueLight};
  color: ${Colors.Blue};
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 13px;
`;

const HeaderSubtitle = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  color: ${Colors.Gray};
`;

const FilterCard = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: flex-end;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 16px;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FilterLabel = styled.span`
  font-size: 12px;
  color: ${Colors.Gray};
`;

const FilterCheckbox = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 6px;
`;

const CenteredPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  background: ${Colors.White};
  border: 1px solid ${Colors.SecondLightGray};
  border-radius: 12px;
  padding: 16px 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px ${Colors.BlueShadow};
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${Colors.Blue};
    box-shadow: 0 6px 20px ${Colors.BlueShadowRow};
  }
`;

const CardTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const TitleBlock = styled.div`
  min-width: 0;
`;

const Title = styled.div`
  ${getFont({ fontSize: 16, lineHeight: 22, familyWeight: 'semibold' })};
  color: ${Colors.Blue};

  ${Card}:hover & {
    text-decoration: underline;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: ${Colors.Gray};
  margin-top: 2px;
`;

const StatusBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const STATE_COLORS: Record<RecordingState, { bg: string; fg: string }> = {
  orphan: { bg: '#FEF3D8', fg: '#9A6B05' },
  matched: { bg: '#DDF3DD', fg: '#1F7A1F' },
  deleted: { bg: '#FDE3E3', fg: '#B42323' },
};

const StateChip = styled.span<{ state: RecordingState }>`
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ state }) => STATE_COLORS[state].fg};
  background: ${({ state }) => STATE_COLORS[state].bg};
`;

const TooShortChip = styled.span`
  border-radius: 12px;
  padding: 3px 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  color: #92400e;
  background: #fef3c7;
`;

const Chevron = styled.span`
  color: ${Colors.Gray};
  font-size: 16px;
  display: inline-flex;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
  font-size: 13px;
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${Colors.Gray};
`;

const FileChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
`;

const FileChip = styled.span`
  border-radius: 6px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 600;
  color: ${Colors.Black};
  background: ${Colors.LightGray};
`;

const Divider = styled.div`
  height: 1px;
  background: ${Colors.LightGray};
  margin: auto 0 14px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;
